<!--
  The header sits on the band rather than above it.

  Nocturne has no sticky chrome except the app's own tab bar, and a fixed strip
  with a blurred fill would be the loudest, most restless thing on a page whose
  design system specifies no motion at all. So this scrolls away like everything
  else, and while it is on screen it is transparent: the hero's band runs behind
  it, unbroken.

  `onBand` is false on the terms and privacy routes, which have no band. There
  the same markup takes ground ink instead.
-->
<!--
  When the mobile menu is open the header needs a surface of its own. It has
  none by design: on the landing page it is absolutely positioned over the
  hero, so an open menu with a transparent background paints its links
  straight on top of the hero copy and both become unreadable.

  On the band the panel is flat `--color-section` rather than the band
  gradient, because that is what the app does for a sheet: the band is a
  gradient, a sheet over it is not. Off the band it is the raised surface.
  Either way it clears at `lg`, where the menu is hidden and the header is
  transparent again, so rotating a phone into a wide landscape cannot leave a
  stray panel behind.
-->
<header
  class="relative z-30"
  [class]="
    showMenu
      ? (onBand()
          ? 'bg-section border-b border-neutral-100/15 lg:border-transparent'
          : 'bg-surface border-b border-line lg:border-transparent') +
        ' lg:bg-transparent'
      : ''
  "
>
  <!--
    Three parts, evenly weighted: mark on the left, links taking the middle,
    controls on the right.

    This used to be `ml-auto` on the nav AND `ml-auto` on the controls. Two
    auto margins in a row do not share the free space, they both consume what
    is left at the point they appear, so every spare pixel collapsed into one
    gap beside the logo and the right-hand cluster ended up squeezed against
    the edge. `flex-1` on the nav puts the free space inside the nav instead,
    where it splits either side of the links.
  -->
  <div
    class="mx-auto flex max-w-[1180px] items-center gap-8 px-8 py-8"
    [class]="onBand() ? 'text-neutral-100' : 'text-ink'"
  >
    <a
      [href]="homeHref"
      class="flex flex-none items-center"
      aria-label="Ledgerly"
    >
      <layout-logo [onBand]="onBand()" />
    </a>

    <nav class="hidden flex-1 items-center justify-center gap-10 lg:flex">
      @for (link of navLinks; track link.path) {
      <a
        [routerLink]="['/']"
        [fragment]="link.path.substring(1)"
        class="text-label"
        [class]="onBand() ? 'text-accent-200' : 'text-ink-2'"
        >{{ link.label }}</a
      >
      }
    </nav>

    <div class="ml-auto flex flex-none items-center gap-4 lg:ml-0">
      <button
        type="button"
        class="flex h-10 w-10 items-center justify-center rounded-pill"
        [class]="onBand() ? 'text-accent-200' : 'text-ink-2'"
        (click)="toggleTheme()"
        [attr.aria-label]="themeToggleLabel"
      >
        <ng-icon
          [name]="theme === 'light' ? 'radixMoon' : 'radixSun'"
          size="18"
        />
      </button>

      <!--
        A plain <details> menu: no overlay library, no focus trap to get wrong,
        it closes on Escape, and it works before the JavaScript arrives.
      -->
      <details class="relative">
        <summary
          class="flex h-10 cursor-pointer list-none items-center gap-3 rounded-pill px-3 text-label [&::-webkit-details-marker]:hidden"
          [class]="onBand() ? 'text-accent-200' : 'text-ink-2'"
          [attr.aria-label]="languageLabel"
        >
          <ng-icon name="radixGlobe" size="18" />
          <span class="hidden sm:inline">{{ currentLocale.label }}</span>
        </summary>

        <ul
          class="absolute right-0 top-12 z-40 min-w-[180px] overflow-hidden rounded-lg border border-line bg-surface py-2 shadow-md"
        >
          @for (locale of locales; track locale.code) {
          <li>
            <a
              [href]="locale.path"
              [attr.hreflang]="locale.code"
              class="flex items-center justify-between gap-8 px-6 py-3 text-label text-ink"
            >
              <span>{{ locale.label }}</span>
              @if (locale.code === currentLocale.code) {
              <span class="text-accent" aria-hidden="true">·</span>
              }
            </a>
          </li>
          }
        </ul>
      </details>

      <!-- On the band the CTA keeps its dark construction; off it, it inverts
           (spec §F.2.3). -->
      <a
        [href]="appStoreLink"
        target="_blank"
        rel="noopener"
        class="hidden h-10 items-center px-6 text-label sm:inline-flex"
        [class]="onBand() ? 'cta-on-band' : 'cta-primary'"
        (click)="trackDownloadClick('header')"
        i18n="@@headerDownload"
        >Get Ledgerly</a
      >

      <button
        type="button"
        class="flex h-10 w-10 items-center justify-center lg:hidden"
        [class]="onBand() ? 'text-accent-200' : 'text-ink-2'"
        [attr.aria-label]="menuLabel"
        [attr.aria-expanded]="showMenu"
        (click)="showMenu = !showMenu"
      >
        <ng-icon
          [name]="showMenu ? 'radixChevronUp' : 'radixHamburgerMenu'"
          size="22"
        />
      </button>
    </div>
  </div>

  @if (showMenu) {
  <nav class="mx-auto max-w-[1180px] px-8 pb-8 lg:hidden">
    <div
      class="flex flex-col gap-6 border-t pt-8"
      [class]="onBand() ? 'border-neutral-100/15' : 'border-line'"
    >
      @for (link of navLinks; track link.path) {
      <a
        [routerLink]="['/']"
        [fragment]="link.path.substring(1)"
        class="text-row"
        [class]="onBand() ? 'text-accent-200' : 'text-ink-2'"
        (click)="showMenu = false"
        >{{ link.label }}</a
      >
      }
      <a
        [href]="appStoreLink"
        target="_blank"
        rel="noopener"
        class="mt-4 inline-flex h-10 w-fit items-center px-6 text-label"
        [class]="onBand() ? 'cta-on-band' : 'cta-primary'"
        (click)="trackDownloadClick('header-mobile')"
        i18n="@@headerDownloadMobile"
        >Get Ledgerly</a
      >
    </div>
  </nav>
  }
</header>
